import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { View, Animated, Keyboard, ScrollView } from 'react-native';
import { Button, Link } from 'LocalGluons';
import { t } from 'Locale';
import Theme from 'Theme';
import { Device } from 'DeviceUtils';
import {
    withFavoriteSettingsContext,
    useFavoriteSettingsContext
} from '../../FavoriteSettingsContext';
import defaultStyles from './FavoritePageWrapper.jss';

const FavoritePageWrapper = ({
    children,
    onCancelCallback,
    onSaveCallback,
    onSelectAllCallback,
    onDeselectAllCallback,
    dataChanged,
    setDataChanged,
    activeCategory,
    selectedAll
}) => {
    const [hideButtons, setHideButtons] = useState(false);
    const { orderedFavoriteList } = useFavoriteSettingsContext();

    const prevActiveCategory = useRef(activeCategory);

    const bottom = new Animated.Value(0);

    const keyboardDidShow = () => {
        setHideButtons(true);
    };

    const keyboardDidHide = () => {
        setHideButtons(false);
    };

    useEffect(() => {
        const keyboardDidShowRemove = Keyboard.addListener('keyboardDidShow', keyboardDidShow);
        const keyboardDidHideRemove = Keyboard.addListener('keyboardDidHide', keyboardDidHide);

        return () => {
            keyboardDidShowRemove.remove();
            keyboardDidHideRemove.remove();
        };
    }, []);

    useEffect(() => {
        if (activeCategory !== prevActiveCategory.current) {
            prevActiveCategory.current = activeCategory;
        }
    }, [activeCategory]);

    const hasSelectAll =
        onSelectAllCallback &&
        typeof onSelectAllCallback === 'function' &&
        onDeselectAllCallback &&
        typeof onDeselectAllCallback === 'function';

    const onCancelButtonPressed = () => {
        setDataChanged(false);
        onCancelCallback();
    };

    const onSaveButtonPressed = () => {
        onSaveCallback(orderedFavoriteList);
    };

    return (
        <View style={defaultStyles.backgroundContainer}>
            {children}
            {!hideButtons && (
                <Animated.View
                    style={[
                        defaultStyles.buttonsContainer,
                        { marginBottom: bottom },
                        hasSelectAll ? defaultStyles.buttonsContainerSelectAll : {}
                    ]}
                >
                    {hasSelectAll && !selectedAll && (
                        <View>
                            <Link
                                label={t('RECORDINGS.SELECT_ALL')}
                                onPress={onSelectAllCallback}
                            />
                        </View>
                    )}
                    {hasSelectAll && selectedAll && (
                        <View>
                            <Link
                                label={t('COMMON.DESELECT_ALL')}
                                onPress={onDeselectAllCallback}
                            />
                        </View>
                    )}
                    <View style={defaultStyles.buttonsWrapper}>
                        <Button
                            style={{
                                button: defaultStyles.containerButton
                            }}
                            isSecondary
                            title={t('COMMON.BTN_CANCEL')}
                            onPress={onCancelButtonPressed}
                            testID="cancel_button"
                        />
                        <Button
                            style={{
                                button: defaultStyles.containerButton
                            }}
                            title={t('SETTINGS.SAVE')}
                            onPress={onSaveButtonPressed}
                            testID="save_button"
                            isDisabled={!dataChanged}
                        />
                    </View>
                </Animated.View>
            )}
        </View>
    );
};

FavoritePageWrapper.defaultProps = {
    onSelectAllCallback: null,
    onDeselectAllCallback: null,
    activeCategory: null,
    selectedAll: false
};

FavoritePageWrapper.propTypes = {
    children: PropTypes.node.isRequired,
    onCancelCallback: PropTypes.func.isRequired,
    onSaveCallback: PropTypes.func.isRequired,
    onSelectAllCallback: PropTypes.func,
    onDeselectAllCallback: PropTypes.func,
    dataChanged: PropTypes.bool.isRequired,
    setDataChanged: PropTypes.func.isRequired,
    activeCategory: PropTypes.number,
    selectedAll: PropTypes.bool
};

const FavoritePageWrapperWithFavoriteSettingsContext =
    withFavoriteSettingsContext(FavoritePageWrapper);

export { FavoritePageWrapperWithFavoriteSettingsContext as FavoritePageWrapper };
